import React, {useEffect} from 'react';
import {Form, Field, useFormState} from 'react-final-form';
import {composeValidators, required, email, limit} from '@form/helpers/ValidateHelper';

function ResumeInputField({input, className, placeholder, autoComplete, inputProps, disabled, meta: {touched, error, dirtySinceLastSubmit, submitError}}) {
    const hasError = (touched && error) || (!dirtySinceLastSubmit && submitError);

    return <input
        {...input}
        {...inputProps}
        className={className + (hasError ? ' resume-auth__input--error' : '')}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
    />;
}

function ResumeError({name}) {
    return <Field
        name={name}
        subscribe={{touched: true, error: true, submitError: true, dirtySinceLastSubmit: true}}
        render={({meta: {touched, error, submitError, dirtySinceLastSubmit}}) => {
            const visibleError = ((touched && error) || (!dirtySinceLastSubmit && submitError))
                ? (error || submitError)
                : '';
            return <div className="resume-auth__error">{visibleError}</div>;
        }}
    />;
}

function EmailTracker({emailRef}) {
    const {values} = useFormState({subscription: {values: true}});
    useEffect(() => {
        if (emailRef) emailRef.current = values.email || '';
    }, [values.email]);
    return null;
}

function ResumeLoginForm({onSubmit, onForgotPassword, emailRef}) {
    return (
        <Form onSubmit={onSubmit}>
            {({handleSubmit, submitting}) => (
                <form onSubmit={handleSubmit} className="resume-auth__form" noValidate>
                    <EmailTracker emailRef={emailRef} />
                    <div className="resume-auth__form-group">
                        <label className="resume-auth__label">
                            Email <span className="resume-auth__required">*</span>
                        </label>
                        <Field
                            name="email"
                            type="email"
                            component={ResumeInputField}
                            placeholder="e.g. Email@address.com"
                            className="resume-auth__input"
                            autoComplete="email"
                            inputProps={{
                                inputMode: 'email',
                                autoCorrect: 'off',
                                spellCheck: 'false',
                                autoCapitalize: 'none',
                            }}
                            validate={composeValidators(
                                required('Please provide an email'),
                                email('Please provide a valid email'),
                                limit('Maximum {limit} characters allowed. You\'ve used {count}.', 320)
                            )}
                        />
                        <ResumeError name="email" />
                    </div>

                    <div className="resume-auth__form-group">
                        <label className="resume-auth__label">
                            Password <span className="resume-auth__required">*</span>
                        </label>
                        <Field
                            name="password"
                            type="password"
                            component={ResumeInputField}
                            placeholder="Your password"
                            className="resume-auth__input"
                            autoComplete="current-password"
                            validate={composeValidators(
                                required('Please provide a password'),
                                limit('Maximum {limit} characters allowed. You\'ve used {count}.', 255)
                            )}
                        />
                        <ResumeError name="password" />
                        <a
                            onClick={onForgotPassword}
                            className="resume-auth__forgot-link"
                            role="button"
                            tabIndex={0}
                        >
                            Forgot password?
                        </a>
                    </div>

                    <div className="resume-auth__form-actions">
                        <button
                            type="submit"
                            className="resume-auth__btn"
                            disabled={submitting}
                        >
                            {submitting ? 'Signing in...' : 'Sign In'}
                        </button>
                    </div>
                </form>
            )}
        </Form>
    );
}

export default ResumeLoginForm;
